import { Clock } from "lucide-react";
import { QuickVoteCluster } from "./QuickVoteCluster.jsx";
import { SlotIndicator } from "./SlotIndicator.jsx";

/**
 * SchedulerCalendarSlot — week+day calendar slot cell with quick-vote header.
 *
 * Renders the timed-slot UI (week or day calendar view) with a top-right
 * quick-vote cluster identical to the month view (delegated to QuickVoteCluster).
 *
 * This is a pure render component. The parent (SchedulerPage's EventCell)
 * computes `vote`, `canInteract`, etc. from its closure and supplies the
 * vote callbacks. The component does not read context or hooks.
 *
 * Chunk G: visual signals (vote-fill / engine-glow / dashed-auto-voted border)
 * live on the outer .rbc-event via SchedulerPage's eventPropGetter. This
 * component is structural-only — it positions content + surfaces the
 * SlotIndicator icon, but no longer applies any cell-tint.
 *
 * Props:
 *  - event: { id, start, end, rangeLabel, timeLabel, preferredCount, feasibleCount,
 *             preferredVoters, feasibleVoters, ... }
 *  - viewKey: "week" | "day" — controls testid prefix + minor sizing
 *  - durationMinutes: number — used to pick the size tier
 *  - vote: "FEASIBLE" | "PREFERRED" | null
 *  - canInteract: boolean — gates the buttons (false if isPast/isLocked/!canVote/noTimesWork)
 *  - noTimesWork: boolean — hides the buttons entirely (matches month branch)
 *  - hasMultipleSlots: boolean — switches to single cycle button when true
 *  - onSetVote: (slotId, nextValue: "FEASIBLE"|"PREFERRED"|null) => void
 *  - avatarStack: React component used for the voter stacks (injected so
 *    SchedulerPage's color-mapped variant can be reused)
 *  - voteAutoReason: voteAutoReason[slotId] | null — drives the SlotIndicator
 *    icon. (Cell-tint moved to outer .rbc-event in Chunk G.)
 *  - voteAutoSource?: string|null — voteAutoSource[slotId]; forwarded to
 *    SlotIndicator so it can synthesize the Chunk G6 urgent override state.
 *  - onRestore?: (slotId) => void|Promise<void> — Chunk G6 Restore handler;
 *    forwarded to SlotIndicator.
 *  - onConfirm?: (slotId) => void|Promise<void> — Chunk G6 Confirm handler;
 *    forwarded to SlotIndicator.
 *  - isPast?: boolean — Chunk G3: when true, suppress the SlotIndicator and
 *    render a small ⏰ "Past" marker in its place. The desaturate visual is
 *    applied to the outer .rbc-event by SchedulerPage's eventPropGetter, so
 *    we don't re-apply the .scheduler-slot-past class on the inner wrapper
 *    (would double-desaturate).
 *  - schedulerId?: string — passed to SlotIndicator for deep links
 *  - displayTimeZone?: string — passed to SlotIndicator for time formatting
 *  - groupNames?: object — passed to SlotIndicator for no-group-match popover
 */
export function SchedulerCalendarSlot(props) {
  const {
    event,
    viewKey,
    durationMinutes,
    vote,
    canInteract,
    noTimesWork,
    hasMultipleSlots,
    onSetVote,
    voteAutoReason = null,
    voteAutoSource = null,
    onRestore,
    onConfirm,
    isPast = false,
    schedulerId,
    displayTimeZone,
    groupNames,
    // Per-slot vote tallies (★/✓ counts) and voter avatar stacks are vote
    // DETAILS — gated by the per-viewer reveal trigger. When false, the cell
    // still shows the viewer's own quick-vote controls + SlotIndicator, but not
    // who voted for what (prevents reading hidden per-slot counts via calendar).
    showVoteDetails = true,
  } = props;
  // Note: re-bound off `props` (rather than destructured) so eslint's
  // base no-unused-vars doesn't flag the JSX-only reference. The repo's
  // eslint config has no react plugin to track JSX element usage.
  const AvatarStackComponent = props.avatarStack;
  const preferredCount = event.preferredCount ?? 0;
  const feasibleCount = event.feasibleCount ?? 0;
  const rangeLabel = event.rangeLabel || event.timeLabel;
  const slotTestId = `${viewKey}-slot-${event.id}`;
  const voteState = noTimesWork ? "unavailable" : vote ? vote.toLowerCase() : "none";

  const indicatorKind = voteAutoReason?.kind || null;

  // Chunk G3: when the slot is in the past, the engine signal becomes
  // irrelevant — suppress the SlotIndicator and render a ⏰ "Past" marker in
  // its place. (The desaturate visual is applied to the outer .rbc-event via
  // SchedulerPage's eventPropGetter, so we don't re-apply the
  // .scheduler-slot-past class here.)
  const indicator = isPast ? (
    <span
      data-testid={`${viewKey}-slot-past-marker-${event.id}`}
      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400"
    >
      <Clock className="h-3 w-3" aria-hidden="true" />
      <span>Past</span>
    </span>
  ) : indicatorKind ? (
    <SlotIndicator
      reason={voteAutoReason}
      voteAutoSource={voteAutoSource}
      onRestore={onRestore}
      onConfirm={onConfirm}
      schedulerId={schedulerId}
      slotId={event.id}
      displayTimeZone={displayTimeZone}
      groupNames={groupNames}
      testId={`${viewKey}-slot-indicator-${event.id}`}
    />
  ) : null;

  const cluster = noTimesWork ? null : (
    <QuickVoteCluster
      slotId={event.id}
      vote={vote}
      canInteract={canInteract}
      hasMultipleSlots={hasMultipleSlots}
      ariaLabelTarget={rangeLabel}
      testIdPrefix={`${viewKey}-slot`}
      onSetVote={onSetVote}
    />
  );

  const tierTextClass = viewKey === "week" ? "text-[10px]" : "text-[11px]";
  const stackSize = viewKey === "week" ? 14 : 16;
  const stackMax = viewKey === "week" ? 3 : 4;

  // Tier 1: very short (≤30m). Single-line layout — keep range + quick-vote on
  // one row; counts compressed into the same line below if there's room. The
  // SlotIndicator (when present) sits inline next to the count text since the
  // top row is space-constrained by the cluster.
  if (durationMinutes <= 30) {
    return (
      <div
        className="space-y-0.5"
        data-testid={slotTestId}
        data-slot-id={event.id}
        data-vote-state={voteState}
      >
        <div className="flex items-start justify-between gap-2">
          <div
            className={`min-w-0 truncate font-semibold leading-tight text-white/95 ${tierTextClass}`}
          >
            {rangeLabel}
          </div>
          {cluster}
        </div>
        <div className={`flex items-center justify-between gap-1 leading-tight text-white/90 ${tierTextClass}`}>
          {showVoteDetails ? (
            <span data-testid={`${viewKey}-slot-tally-${event.id}`}>
              ★ {preferredCount} · ✓ {feasibleCount}
            </span>
          ) : (
            // Layout spacer: keeps the SlotIndicator right-aligned (justify-between).
            <span />
          )}
          {indicator}
        </div>
      </div>
    );
  }

  // Tier 2: short (<120m). Two-row layout — title row with quick-vote, counts
  // share a single row below. SlotIndicator drops into the right side of the
  // counts row.
  if (durationMinutes < 120) {
    return (
      <div
        className="space-y-1"
        data-testid={slotTestId}
        data-slot-id={event.id}
        data-vote-state={voteState}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 truncate text-xs font-semibold text-white/95">{rangeLabel}</div>
          {cluster}
        </div>
        <div className={`flex items-center gap-2 text-white/90 ${tierTextClass}`}>
          {showVoteDetails && (
            <span
              className="flex items-center gap-2"
              data-testid={`${viewKey}-slot-tally-${event.id}`}
            >
              <span>★ {preferredCount}</span>
              <AvatarStackComponent users={event.preferredVoters} max={stackMax} size={stackSize} />
              <span className="ml-1">✓ {feasibleCount}</span>
              <AvatarStackComponent users={event.feasibleVoters} max={stackMax} size={stackSize} />
            </span>
          )}
          {indicator && <span className="ml-auto">{indicator}</span>}
        </div>
      </div>
    );
  }

  // Tier 3: long (≥120m). Three-row layout — title row with quick-vote, then
  // preferred row, then feasible row (stacks individually per row). The
  // SlotIndicator pins to the right of the feasible row so it stays visible
  // without competing with the title row's quick-vote cluster.
  return (
    <div
      className="space-y-1"
      data-testid={slotTestId}
      data-slot-id={event.id}
      data-vote-state={voteState}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 truncate text-xs font-semibold text-white/95">{rangeLabel}</div>
        {cluster}
      </div>
      {showVoteDetails && (
        <div
          className={`flex items-center gap-2 text-white/90 ${tierTextClass}`}
          data-testid={`${viewKey}-slot-tally-${event.id}`}
        >
          <span>★ {preferredCount}</span>
          <AvatarStackComponent users={event.preferredVoters} max={stackMax} size={stackSize} />
        </div>
      )}
      <div className={`flex items-center gap-2 text-white/90 ${tierTextClass}`}>
        {showVoteDetails && (
          <>
            <span>✓ {feasibleCount}</span>
            <AvatarStackComponent users={event.feasibleVoters} max={stackMax} size={stackSize} />
          </>
        )}
        {indicator && <span className="ml-auto">{indicator}</span>}
      </div>
    </div>
  );
}
